import gql from 'graphql-tag';
import React, { useCallback, useMemo, useState } from 'react';
import { View, Platform } from 'react-native';

import { useUserContext } from '../../../common/utils/context';
import {
	getCurrentServiceRunDayEnd,
	resolveServiceRunListQueryVariables,
} from '../../../common/utils/serviceRuns/serviceRunListDateFilter';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import ServiceRunList from './ServiceRunList';
import { UserSelector } from '../../components/selector';


// showEveryRun: the redesigned All Runs tab of a rostered picker, who sees every
// run even when their account also has the Driver role.
// onRunPress(run): optional; returns true when it opened the run itself
// (otherwise the run opens in ServiceRunDetailView).
const ServiceRunListView = ({ refetchModeData, showEveryRun = false, onRunPress }) => {
	const styles = useThemedStyles(createStyles);
	const {
		isDriver,
		user,
		tenant,
		tenantSettings,
	} = useUserContext();
	const [driverFilter, setDriverFilter] = useState(null);
	const isOwnRunList = isDriver && !showEveryRun;

	const resolvedOverrideHideFutureRunsFromDriversSetting = user?.overrideHideFutureRunsFromDriversSetting
		? user?.hideFutureRunsFromDrivers
		: tenantSettings?.hideFutureRunsFromDrivers;
	const shouldRefreshPlannedToDate = isOwnRunList && resolvedOverrideHideFutureRunsFromDriversSetting;
	const queryVariablesResolver = useCallback(
		variables => resolveServiceRunListQueryVariables(variables, tenant?.timezone, shouldRefreshPlannedToDate),
		[shouldRefreshPlannedToDate, tenant?.timezone],
	);

	const filters = useMemo(() => {
		const filterObject = {};

		if (isOwnRunList && user?.id) {
			filterObject.driver = user.id;
			// hide runs from drivers until the day of their run
			if (resolvedOverrideHideFutureRunsFromDriversSetting) {
				filterObject.plannedToDate = getCurrentServiceRunDayEnd(tenant?.timezone);
			}
		}

		if (driverFilter?.id) {
			filterObject.driver = driverFilter.id;
		}

		return filterObject;
	}, [isOwnRunList, user?.id, tenant?.timezone, resolvedOverrideHideFutureRunsFromDriversSetting, driverFilter?.id]);

	// Don't render until user context is available to avoid filter instability
	if (!user && isDriver) {
		return null;
	}

	return (
		<View style={styles.container}>
			<ServiceRunList
				query={GET_SERVICE_RUNS_NATIVE}
				queryVariablesResolver={queryVariablesResolver}
				initialFilter={filters}
				initialSortBy="planned"
				initialSortDirection={-1}
				initialPerPage={10}
				listId="m_sr_list"
				refetchModeData={refetchModeData}
				onRunPress={onRunPress}
				customControls={
					!isDriver && (
						<UserSelector
							value={driverFilter}
							onChange={newUser => {
								if (newUser?.id === driverFilter?.id) {
									setDriverFilter(null);
								}
								else {
									setDriverFilter(newUser);
								}
							}}
							placeholder="Select driver"
							buttonStyle={styles.driverFilter}
						/>
					)
				}
			/>
		</View>
	);
};

export default React.memo(ServiceRunListView);

export const GET_SERVICE_RUNS_NATIVE = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: ServiceRunPageFilter
	) {
		getServiceRuns(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				id
				planned

				route {
					id
					name
				}

				driver {
					id
					username
					firstname
					lastname
				}

				vehicle {
					id
					name
					serviceDue
				}

				serviceDockets {
					id
					status
					inventoryCheck
				}
			}
			count
		}
	}
`;

const createStyles = ({ ui: { colors, space } }) => ({
	container: {
		backgroundColor: colors.canvas,
		flex: 1,
		...(Platform.OS === 'web' ? { height: '100%' } : {}),
	},
	// Share the row with the text filter (flex 1) so neither collapses nor runs
	// under Settings; the slightly larger share keeps "Select driver" on one line.
	driverFilter: {
		flex: 1.25,
		minWidth: 0,
		marginLeft: space.sm,
	},
});
